import { useState } from 'react';
import { Dropdown } from 'antd';
import {
  DotsVertical,
  Edit03,
  MessageTextSquare01,
  Trash01,
  File02,
  Bell01,
  BellOff01,
  ClockRewind,
  AmbientAi,
  CheckCircle,
  RefreshCcw01,
  Send01,
} from '@insight-health-ai/roseate/icons';
import {
  Button,
  DrawerProps,
  Modal,
  toast,
} from '@insight-health-ai/roseate/components';
import { MenuItemType } from 'antd/es/menu/interface';
import {
  NonOptional,
  PromiseOrValue,
  useScreenSize,
} from '@insight-health-ai/roseate/utils';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { Appointment } from 'src/models';
import {
  useDeleteAppointment,
  useGetAppointmentDetails,
  useOptOutAppointmentNotification,
  useConvertAppointmentToAuraOnly,
  useMarkAppointmentAsProcessed,
  useUnmarkAppointmentAsProcessed,
  useSendAppointmentReminder,
} from 'src/hooks/appointment';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { useNavigate } from 'react-router-dom';
import { NotificationHistoryDrawer } from '../details/notificationHistory';
import { getPractitionerName } from 'src/utils/practitioner';
import { analytics } from 'src/services';
import {
  getAppointmentRowAnalyticsProps,
  trackOpenedCareConnectSummary,
} from './helpers';

export type UseAppointmentActionsProps = {
  appointment?: Appointment;
  appointmentUuid?: Appointment['appt_uuid'];
  onViewNotificationHistory?: () => PromiseOrValue<void>;
  onEditAppointment?: (appointment: Appointment) => PromiseOrValue<void>;
  onDeleteCallback?: () => PromiseOrValue<void>;
};

export const useAppointmentActions = ({
  appointment: appointmentProp,
  appointmentUuid,
  onViewNotificationHistory,
  onEditAppointment,
  onDeleteCallback,
}: UseAppointmentActionsProps) => {
  const { data: appointmentData } = useGetAppointmentDetails({
    appt_uuid: appointmentUuid!,
  });
  const { mutateAsync: deleteAppointment } = useDeleteAppointment();
  const {
    mutateAsync: optOutAppointmentNotification,
    isPending: isMutingAppointmentNotification,
  } = useOptOutAppointmentNotification();
  const { mutateAsync: convertAppointmentToAuraOnly } =
    useConvertAppointmentToAuraOnly();
  const { mutateAsync: markAsProcessed, isPending: isMarkingProcessed } =
    useMarkAppointmentAsProcessed();
  const { mutateAsync: unmarkAsProcessed, isPending: isUnmarkingProcessed } =
    useUnmarkAppointmentAsProcessed();
  const { mutateAsync: sendAppointmentReminder, isPending: isSendingReminder } =
    useSendAppointmentReminder();
  const { isEntitled: hasVCAProcessedWorkflowAccess } = useFeatureEntitlements(
    'vca-processed-status'
  );
  const appointment = appointmentData || appointmentProp;
  const { mediaMin } = useScreenSize();
  const actionCountToRender = mediaMin('sm') ? 2 : 0;
  const { isAdmin } = useGetCurrentPractitioner();
  const { user } = useAuth();
  const navigate = useNavigate();

  const drawerActionProps = {
    list: [],
    overflowMenu: { after: actionCountToRender },
  } as NonOptional<
    Required<DrawerProps>['headerActions'],
    'overflowMenu' | 'list'
  >;

  if (!appointment) {
    return { actions: drawerActionProps };
  }

  const chatCompletionStatus = appointment.chat_completion_status;
  const isExpired = appointment.isExpired;
  const isOptedOut = appointment.is_opted_out;
  const hasLlmChatUrl = !!appointment.llm_chat_url;

  const isUnavailable = chatCompletionStatus?.toLowerCase() === 'unavailable';
  const isCompleted = chatCompletionStatus === 'Completed';
  const isNew =
    chatCompletionStatus === 'New' || chatCompletionStatus === 'new';

  const canEditAppointment =
    (!isExpired && !isCompleted) || (isAdmin && (isCompleted || isExpired));

  const canOpenChatAsPatient = !isExpired && !isUnavailable && hasLlmChatUrl;

  const canConvertToAuraEncounter = isNew && hasLlmChatUrl && !isExpired;

  const canMuteNotifications = !isUnavailable && !isCompleted && !isOptedOut;

  const canUnmuteNotifications = isOptedOut;

  const intakeEligibleForProcessedWorkflow =
    isCompleted ||
    chatCompletionStatus === 'partial_completed' ||
    chatCompletionStatus === 'InProgress';

  const canMarkAsProcessed =
    hasVCAProcessedWorkflowAccess &&
    intakeEligibleForProcessedWorkflow &&
    !appointment.is_processed;

  const canUnmarkAsProcessed =
    hasVCAProcessedWorkflowAccess &&
    intakeEligibleForProcessedWorkflow &&
    appointment.is_processed;

  const revertProcessedLabelSuffix =
    chatCompletionStatus === 'Completed'
      ? 'completed'
      : chatCompletionStatus === 'InProgress'
        ? 'in progress'
        : 'partial summary';

  const hasNotificationChannelEnabled =
    !!appointment.settings?.enable_patient_email_notifications ||
    !!appointment.settings?.enable_patient_sms_notifications;

  const canSendIntakeReminder =
    hasNotificationChannelEnabled &&
    !isExpired &&
    !isUnavailable &&
    !isCompleted;

  if (canSendIntakeReminder) {
    drawerActionProps.list.push({
      disabled: isSendingReminder,
      icon: <Send01 />,
      label: 'Send intake reminder',
      variant: 'outlined',
      appearance: 'secondary',
      onClick: async () => {
        analytics.track('Send Intake Reminder', {
          ...getAppointmentRowAnalyticsProps(appointment),
          email: user?.email,
        });
        try {
          await sendAppointmentReminder({
            appt_uuid: appointment.appt_uuid,
            appointment_id: appointment.appointment_id,
            sent_by: user?.email ?? '',
          });
          toast.success('Reminder sent to patient');
        } catch {
          toast.error(
            'Failed to send intake reminder. Please try again later.'
          );
        }
      },
    });
  }

  drawerActionProps.list.push({
    icon: <File02 />,
    label: 'View summary',
    variant: 'outlined',
    appearance: 'secondary',
    onClick: () => {
      trackOpenedCareConnectSummary(appointment, user?.email, 'menu');
      navigate(`/schedule/${appointment.appt_uuid}`);
    },
  });

  if (canOpenChatAsPatient) {
    drawerActionProps.list.push({
      icon: <MessageTextSquare01 />,
      label: 'Open chat as patient',
      variant: 'outlined',
      appearance: 'secondary',
      onClick: () => {
        const { llm_chat_url } = appointment;
        if (llm_chat_url) {
          analytics.track('Opened Lumi Chat', {
            ...getAppointmentRowAnalyticsProps(appointment),
            email: user?.email,
            loginType: 'patient',
          });
          window.open(llm_chat_url, '_blank');
        }
      },
    });
  }

  if (canConvertToAuraEncounter) {
    drawerActionProps.list.push({
      icon: <AmbientAi />,
      label: 'Convert to Aura Encounter',
      variant: 'outlined',
      appearance: 'secondary',
      onClick: async () => {
        Modal.confirm({
          appearance: 'error',
          title: 'Confirmation',
          content: (
            <span>
              Are you sure you want to convert this appointment to Aura
              Encounter?
              <br />
              <br />
              <span className="font-bold">Practitioner Name:</span>{' '}
              {getPractitionerName({
                first_name: appointment.practitioner_firstName,
                last_name: appointment.practitioner_lastName,
                title: appointment.practitioner_title,
              })}
              <br />
              <span className="font-bold">Patient Name:</span>{' '}
              {[appointment.patient_firstName, appointment.patient_lastName]
                .filter(Boolean)
                .join(' ')}
              <br />
              <br />
              <span className="font-bold">This action can not be undone.</span>
            </span>
          ),
          primaryAction: {
            label: 'Proceed',
            onClick: async () => {
              try {
                analytics.track('Converted Appointment to Aura Only', {
                  ...getAppointmentRowAnalyticsProps(appointment),
                  email: user?.email,
                });
                await convertAppointmentToAuraOnly({
                  appt_uuid: appointment.appt_uuid,
                });
                toast.success('Appointment converted to Aura Encounter');
              } catch (error) {
                toast.error(
                  'Failed to convert appointment to Aura Encounter. Please try again later.'
                );
                throw error;
              }
            },
          },
        });
      },
    });
  }

  if (canEditAppointment && onEditAppointment) {
    drawerActionProps.list.push({
      icon: <Edit03 />,
      label: 'Edit appointment',
      variant: 'outlined',
      appearance: 'secondary',
      onClick: () => {
        analytics.track('Clicked Edit Appointment', {
          ...getAppointmentRowAnalyticsProps(appointment),
          email: user?.email,
        });
        return onEditAppointment?.(appointment);
      },
    });
  }

  if (onViewNotificationHistory) {
    drawerActionProps.list.push({
      icon: <ClockRewind />,
      label: 'Show notification history',
      variant: 'outlined',
      appearance: 'secondary',
      onClick: onViewNotificationHistory,
    });
  }

  if (canMarkAsProcessed) {
    drawerActionProps.list.push({
      disabled: isMarkingProcessed,
      icon: <CheckCircle className="text-primary" />,
      label: 'Mark as processed',
      variant: 'outlined',
      appearance: 'secondary',
      onClick: async () => {
        try {
          analytics.track('Marked Appointment As Processed', {
            ...getAppointmentRowAnalyticsProps(appointment),
            email: user?.email,
          });
          await markAsProcessed({
            appointment_id: appointment.appointment_id,
            appt_uuid: appointment.appt_uuid,
          });
          toast.success('Appointment marked as processed');
        } catch {
          toast.error('Error marking appointment as processed');
        }
      },
    });
  }

  if (canUnmarkAsProcessed) {
    drawerActionProps.list.push({
      disabled: isUnmarkingProcessed,
      icon: <RefreshCcw01 />,
      label: `Revert back to ${revertProcessedLabelSuffix}`,
      variant: 'outlined',
      appearance: 'secondary',
      onClick: async () => {
        try {
          analytics.track('Reverted Appointment To Completed', {
            ...getAppointmentRowAnalyticsProps(appointment),
            email: user?.email,
          });
          await unmarkAsProcessed({
            appointment_id: appointment.appointment_id,
            appt_uuid: appointment.appt_uuid,
          });
          toast.success('Appointment status reverted');
        } catch {
          toast.error('Error reverting appointment status');
        }
      },
    });
  }

  if (canMuteNotifications) {
    drawerActionProps.list.push({
      disabled: isMutingAppointmentNotification,
      icon: <BellOff01 />,
      label: 'Mute patient notifications',
      variant: 'outlined',
      appearance: 'danger',
      onClick: async () => {
        analytics.track('Clicked Mute Patient Notifications', {
          ...getAppointmentRowAnalyticsProps(appointment),
          email: user?.email,
        });
        try {
          await optOutAppointmentNotification({
            appt_uuid: appointment.appt_uuid,
            is_opt_out: true,
          });
          toast.success('Patient notifications muted');
        } catch (error) {
          toast.error(
            'Failed to mute patient notifications. Please try again later.'
          );
          throw error;
        }
      },
    });
  }

  if (canUnmuteNotifications) {
    drawerActionProps.list.push({
      icon: <Bell01 />,
      label: 'Unmute patient notifications',
      variant: 'outlined',
      disabled: isMutingAppointmentNotification,
      appearance: 'secondary',
      onClick: async () => {
        analytics.track('Clicked Unmute Patient Notifications', {
          ...getAppointmentRowAnalyticsProps(appointment),
          email: user?.email,
        });
        try {
          await optOutAppointmentNotification({
            appt_uuid: appointment.appt_uuid,
            is_opt_out: false,
          });
          toast.success('Patient notifications unmuted');
        } catch (error) {
          toast.error(
            'Failed to unmute patient notifications. Please try again later.'
          );
          throw error;
        }
      },
    });
  }

  drawerActionProps.list.push({
    icon: <Trash01 />,
    label: 'Remove appointment',
    variant: 'outlined',
    appearance: 'danger',
    onClick: () => {
      analytics.track('Clicked Remove Appointment', {
        ...getAppointmentRowAnalyticsProps(appointment),
        email: user?.email,
      });
      Modal.confirm({
        appearance: 'error',
        title: 'Remove appointment',
        content: (
          <span>
            This appointment has not been completed. Are you sure you want to
            remove the appointment for patient{' '}
            <span className="font-bold">
              {appointment.patient_firstName} {appointment.patient_lastName}
            </span>
            ?
          </span>
        ),
        primaryAction: {
          label: 'Remove',
          onClick: async () => {
            try {
              await deleteAppointment({
                appt_uuid: appointment.appt_uuid,
              });
              toast.success('Appointment removed successfully');
              await onDeleteCallback?.();
            } catch (error) {
              toast.error(
                'Failed to remove appointment. Please try again later.'
              );
              throw error;
            }
          },
        },
      });
    },
  });

  return {
    actions: drawerActionProps,
  };
};

export const AppointmentActionsDropdown = (
  props: UseAppointmentActionsProps
) => {
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
  const { actions: drawerActionProps } = useAppointmentActions({
    ...props,
    onViewNotificationHistory: () => {
      setHistoryModalOpen(true);
    },
    onDeleteCallback: () => {
      setHistoryModalOpen(false);
    },
  });
  return (
    <>
      <Dropdown
        trigger={['click']}
        menu={{
          items: drawerActionProps.list.map((item, index) => {
            return {
              ...item,
              key: index,
              danger: item.appearance === 'danger',
            };
          }) as MenuItemType[],
        }}
      >
        <Button appearance="secondary" variant="text" icon={<DotsVertical />} />
      </Dropdown>
      {historyModalOpen && (
        <NotificationHistoryDrawer
          appointmentUuid={
            props.appointmentUuid || props.appointment?.appt_uuid!
          }
          open={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          onEditAppointment={props.onEditAppointment}
        />
      )}
    </>
  );
};
